import React, { useRef, useEffect, useState } from 'react';

function App() {
    const videoRef = useRef(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [poolSize, setPoolSize] = useState(0);
    const [logs, setLogs] = useState([]);
    const entropyBufferRef = useRef([]);
    const lastSendRef = useRef(Date.now());

    // Función para enviar entropía al backend
    const sendEntropy = async (hexData) => {
        try {
            const response = await fetch('https://segurity.chessbitcoin.com/recibir_entropia', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ entropy_data: hexData })
            });
            const data = await response.json();
            addLog(`📤 Enviado: ${hexData.length/2} bytes | Pool: ${(data.server_pool_kb || 0).toFixed(1)} KB`);
            return data;
        } catch (error) {
            addLog(`❌ Error: ${error.message}`);
        }
    };

    // Función para ver estado del pool
    const checkStatus = async () => {
        try {
            const response = await fetch('https://segurity.chessbitcoin.com/estado');
            const data = await response.json();
            setPoolSize(data.pool_size_bytes || 0);
            addLog(`📊 Pool: ${(data.pool_size_bytes / 1024).toFixed(1)} KB | Listo: ${data.listo_para_clave ? '✅' : '❌'}`);
        } catch (error) {
            addLog(`❌ Status error: ${error.message}`);
        }
    };

    const addLog = (msg) => {
        setLogs(prev => [msg, ...prev].slice(0, 20));
        console.log(msg);
    };

    // Extraer bits de entropía de un frame
    const extractEntropy = (video, width, height) => {
        // Crear canvas temporal
        const canvas = document.createElement('canvas');
        canvas.width = 160;
        canvas.height = 120;
        const ctx = canvas.getContext('2d');
        
        // Dibujar video reducido
        ctx.drawImage(video, 0, 0, 160, 120);
        
        // Obtener píxeles
        const imageData = ctx.getImageData(0, 0, 160, 120);
        const pixels = imageData.data;
        
        // Extraer LSB (Least Significant Bits) de cada canal
        const bits = [];
        for (let i = 0; i < pixels.length; i += 4) {
            bits.push(pixels[i] & 1);     // R
            bits.push(pixels[i+1] & 1);   // G
            bits.push(pixels[i+2] & 1);   // B
        }
        
        // Convertir bits a bytes
        const bytes = [];
        for (let i = 0; i < bits.length; i += 8) {
            if (i + 8 <= bits.length) {
                let byte = 0;
                for (let j = 0; j < 8; j++) {
                    if (bits[i + j]) byte |= (1 << j);
                }
                bytes.push(byte);
            }
        }
        
        return new Uint8Array(bytes);
    };

    // Capturar y procesar frame
    const captureFrame = () => {
        if (!videoRef.current || !isCameraActive) return;
        
        const entropy = extractEntropy(videoRef.current);
        
        // Acumular en buffer
        entropyBufferRef.current.push(...entropy);
        
        // Enviar cada 2 segundos o cuando hay 1024 bytes
        const now = Date.now();
        if (entropyBufferRef.current.length >= 1024 || (now - lastSendRef.current) >= 2000) {
            if (entropyBufferRef.current.length > 0) {
                const toSend = entropyBufferRef.current.slice(0, 1024);
                entropyBufferRef.current = entropyBufferRef.current.slice(1024);
                
                // Convertir a hex
                const hexString = Array.from(toSend).map(b => b.toString(16).padStart(2, '0')).join('');
                sendEntropy(hexString);
            }
            lastSendRef.current = now;
        }
    };

    // Iniciar captura continua
    useEffect(() => {
        if (isCameraActive) {
            const interval = setInterval(captureFrame, 100); // 10 FPS
            const statusInterval = setInterval(checkStatus, 3000);
            return () => {
                clearInterval(interval);
                clearInterval(statusInterval);
            };
        }
    }, [isCameraActive]);

    // Iniciar cámara
    const startCamera = async () => {
        try {
            addLog('🎥 Solicitando acceso a cámara...');
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { width: 320, height: 240 } 
            });
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                setIsCameraActive(true);
                addLog('✅ Cámara activa! Generando entropía...');
                checkStatus();
            }
        } catch (err) {
            addLog(`❌ Error cámara: ${err.message}`);
        }
    };

    // Resetear pool
    const resetPool = async () => {
        try {
            await fetch('https://segurity.chessbitcoin.com/reset', { method: 'POST' });
            addLog('🔄 Pool reseteado');
            entropyBufferRef.current = [];
            checkStatus();
        } catch (error) {
            addLog(`❌ Reset error: ${error.message}`);
        }
    };

    // Generar clave
    const generateKey = async (type, bytes, format) => {
        try {
            addLog(`🔑 Generando ${type}...`);
            const response = await fetch('https://segurity.chessbitcoin.com/generar_clave', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ longitud: bytes, formato: format })
            });
            const data = await response.json();
            if (data.success) {
                addLog(`✅ ${type}: ${data.clave.substring(0, 32)}...`);
                alert(`${type}\n\n${data.clave}\n\nCopiala y guardala!`);
            } else {
                addLog(`❌ Error: ${data.error}`);
            }
        } catch (error) {
            addLog(`❌ Generate error: ${error.message}`);
        }
    };

    return (
        <div style={{ 
            minHeight: '100vh', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <h1 style={{ color: 'white', textAlign: 'center' }}>🔐 Entropy Collector</h1>
                
                {/* Video */}
                <div style={{ 
                    background: '#000', 
                    borderRadius: '10px', 
                    overflow: 'hidden',
                    marginBottom: '20px'
                }}>
                    <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        style={{ width: '100%', transform: 'scaleX(-1)' }}
                    />
                </div>
                
                {/* Controles */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    {!isCameraActive ? (
                        <button onClick={startCamera} style={buttonStyle('green')}>
                            🎥 Iniciar Cámara
                        </button>
                    ) : (
                        <>
                            <button onClick={resetPool} style={buttonStyle('red')}>
                                🔄 Reset Pool
                            </button>
                            <button onClick={() => generateKey('WiFi', 32, 'hex')} style={buttonStyle('blue')}>
                                🔐 WiFi
                            </button>
                            <button onClick={() => generateKey('SSL', 32, 'base64')} style={buttonStyle('blue')}>
                                🔒 SSL
                            </button>
                            <button onClick={() => generateKey('API Key', 64, 'hex')} style={buttonStyle('blue')}>
                                🚀 API
                            </button>
                            <button onClick={() => generateKey('Bitcoin', 32, 'hex')} style={buttonStyle('blue')}>
                                ₿ Bitcoin
                            </button>
                        </>
                    )}
                </div>
                
                {/* Estado */}
                <div style={{ 
                    background: 'rgba(0,0,0,0.5)', 
                    borderRadius: '10px', 
                    padding: '15px',
                    marginBottom: '20px',
                    color: 'white'
                }}>
                    <div>📊 Pool de Entropía: <strong>{(poolSize / 1024).toFixed(2)} KB</strong></div>
                    <div>🎥 Cámara: <strong>{isCameraActive ? '🟢 Activa' : '🔴 Inactiva'}</strong></div>
                    <div>💾 Buffer local: <strong>{(entropyBufferRef.current.length / 1024).toFixed(2)} KB</strong></div>
                </div>
                
                {/* Logs */}
                <div style={{ 
                    background: 'rgba(0,0,0,0.7)', 
                    borderRadius: '10px', 
                    padding: '15px',
                    color: '#0f0',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    height: '300px',
                    overflowY: 'auto'
                }}>
                    <strong>📋 Logs:</strong><br/>
                    {logs.map((log, i) => (
                        <div key={i}>{log}</div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const buttonStyle = (color) => ({
    background: color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
});

export default App;
